import React from 'react';
import { Box, Flex, Heading, Text } from '@chakra-ui/react';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { IKanbanCard, IKanbanColumn } from '../../../../hooks/useKanban';
import { IEstatuto } from '../../../../hooks/useEstatuto';
import { EstatutoCard } from './EstatutoCard';
import { COLUMN_PALETTE } from '../utils';

interface EstatutoBoardProps {
  columns: IKanbanColumn[];
  estatutoByCardId: Map<number, IEstatuto>;
  // `estatuto` é opcional: cards avulsos (task rápida sem estatuto vinculado) também abrem.
  // eslint-disable-next-line no-unused-vars
  onCardClick: (estatuto: IEstatuto | undefined, card: IKanbanCard) => void;
  // eslint-disable-next-line no-unused-vars
  onDragEnd: (result: DropResult) => void;
  // Baixa (task #492): mesmas colunas da Constituição, porém SEM as cores (neutro).
  hideColors?: boolean;
}

// Paleta neutra usada quando hideColors=true (quadro da Baixa) — sem os fundos/pontos
// coloridos que distinguem a Constituição.
const NEUTRAL_COLORS = {
  bg: 'gray.50',
  border: 'gray.200',
  chip: 'gray.400',
};

export const EstatutoBoard: React.FC<EstatutoBoardProps> = ({
  columns,
  estatutoByCardId,
  onCardClick,
  onDragEnd,
  hideColors = false,
}) => {
  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Flex gap={4} alignItems="stretch" w="full" overflowX="auto" pb={2}>
        {columns.map((column, idx) => {
          const colors = hideColors ? NEUTRAL_COLORS : COLUMN_PALETTE[idx % COLUMN_PALETTE.length];
          return (
            <Box
              key={column.id}
              bg={colors.bg}
              borderRadius="xl"
              borderWidth="1px"
              borderColor={colors.border}
              p={3}
              minH="500px"
              // Largura fixa por coluna + rolagem horizontal no container (overflowX
              // auto). Com muitas colunas (7) o board rola em vez de espremer.
              flex={{ base: '0 0 85vw', md: '0 0 288px' }}
              minW={{ base: '85vw', md: '288px' }}
              display="flex"
              flexDirection="column"
            >
              <Flex align="center" justify="space-between" mb={3} px={1}>
                <Flex align="center" gap={2} flex={1} minW={0}>
                  <Box w="8px" h="8px" borderRadius="full" bg={colors.chip} flexShrink={0} />
                  <Heading
                    as="h3"
                    fontSize="xs"
                    fontWeight="700"
                    color="gray.700"
                    letterSpacing="0.02em"
                    noOfLines={2}
                    lineHeight="1.2"
                  >
                    {column.titulo}
                  </Heading>
                </Flex>
                <Box
                  bg="white"
                  borderWidth="1px"
                  borderColor={colors.border}
                  borderRadius="full"
                  px={2}
                  py={0.5}
                  ml={2}
                >
                  <Text fontSize="11px" fontWeight="700" color={colors.chip}>
                    {column.cards.length}
                  </Text>
                </Box>
              </Flex>

              <Droppable droppableId={`column-${column.id}`} type="card">
                {(provided, snapshot) => (
                  <Flex
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    direction="column"
                    gap={2}
                    flex={1}
                    borderRadius="md"
                    transition="background 0.2s ease"
                    bg={snapshot.isDraggingOver ? 'rgba(255, 255, 255, 0.5)' : 'transparent'}
                  >
                    {column.cards.length === 0 && !snapshot.isDraggingOver ? (
                      <Flex
                        align="center"
                        justify="center"
                        py={8}
                        borderRadius="md"
                        borderStyle="dashed"
                        borderWidth="1px"
                        borderColor={colors.border}
                        bg="rgba(255, 255, 255, 0.4)"
                      >
                        <Text fontSize="11px" color="gray.400" fontStyle="italic">
                          Arraste estatutos aqui
                        </Text>
                      </Flex>
                    ) : (
                      column.cards.map((card, cardIndex) => {
                        const estatuto = estatutoByCardId.get(card.id);
                        return (
                          <Draggable
                            key={card.id}
                            draggableId={`card-${card.id}`}
                            index={cardIndex}
                          >
                            {(dragProvided, dragSnapshot) => (
                              <Box
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                                opacity={dragSnapshot.isDragging ? 0.9 : 1}
                              >
                                <EstatutoCard
                                  cardTitulo={card.titulo}
                                  card={card}
                                  estatuto={estatuto}
                                  // Passa o card DO BOARD junto — o modal genérico precisa do
                                  // IKanbanCard e assim ele nunca é re-resolvido (nem vira null).
                                  // Card avulso (sem estatuto) também abre — `estatuto` vai undefined.
                                  onClick={() => onCardClick(estatuto, card)}
                                />
                              </Box>
                            )}
                          </Draggable>
                        );
                      })
                    )}
                    {provided.placeholder}
                  </Flex>
                )}
              </Droppable>
            </Box>
          );
        })}
      </Flex>
    </DragDropContext>
  );
};
